import { Outlet, NavLink as RouterNavLink, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  FileText,
  Plus,
  LogOut,
  Menu,
  ChevronRight,
  Building2,
  Settings,
  HelpCircle,
  Home,
  X,
} from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCompanyAuth } from '@/contexts/CompanyAuthContext';

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,500;0,600;1,400&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&display=swap');
  :root {
    --c:      #FAF8F4; --c2: #F2EDE3; --c3: #E6DFD1;
    --forest: #1C3830; --fmid: #2B5244;
    --gold:   #C09A40; --gold-bg: #F5EECB;
    --stone:  #796F61; --ink: #18150F; --white: #FFFFFF;
  }
  .cl * { box-sizing: border-box; }
  .cl { font-family: 'DM Sans', sans-serif; color: var(--ink); background: var(--c); }
  .cl-scroll::-webkit-scrollbar { width: 3px; }
  .cl-scroll::-webkit-scrollbar-thumb { background: var(--c3); border-radius: 99px; }

  /* Sidebar */
  .cl-aside {
    width: 252px; flex-shrink: 0;
    background: var(--c); border-right: 1px solid var(--c3);
    display: flex; flex-direction: column;
  }
  @media (max-width: 767px) { .cl-aside { display: none !important; } }

  /* Header */
  .cl-header {
    height: 60px; flex-shrink: 0;
    background: rgba(250,248,244,0.93);
    backdrop-filter: blur(14px);
    border-bottom: 1px solid var(--c3);
    display: flex; align-items: center;
    justify-content: space-between;
    padding: 0 20px; z-index: 20;
  }

  /* Brand bar */
  .cl-brand {
    height: 60px; display: flex; align-items: center;
    padding: 0 18px; border-bottom: 1px solid var(--c3);
    gap: 10px; flex-shrink: 0;
  }
  .cl-mark {
    width: 30px; height: 30px; border-radius: 8px;
    background: var(--forest); display: flex;
    align-items: center; justify-content: center; flex-shrink: 0;
  }
  .cl-brandname {
    font-family: 'Lora', serif; font-weight: 600;
    font-size: 14.5px; color: var(--forest);
    letter-spacing: -.02em;
  }

  /* Section label */
  .cl-sec-label {
    font-size: 10px; font-weight: 700;
    letter-spacing: .09em; text-transform: uppercase;
    color: var(--stone); opacity: .45;
    padding: 14px 11px 6px; display: block;
  }

  /* Nav links */
  .cl-link {
    display: flex; align-items: center; gap: 9px;
    padding: 7.5px 11px; border-radius: 9px;
    font-size: 13.5px; font-weight: 450;
    color: var(--stone); text-decoration: none;
    transition: color .13s, background .13s;
    position: relative; letter-spacing: -.01em;
  }
  .cl-link:hover { color: var(--forest); background: var(--c2); }
  .cl-link.on    { color: var(--forest); background: var(--c2); font-weight: 560; }
  .cl-link.on::after {
    content: ''; position: absolute;
    left: 0; top: 20%; height: 60%; width: 3px;
    background: var(--gold); border-radius: 0 3px 3px 0;
  }

  /* Notification pill */
  .cl-pill {
    margin-left: auto;
    background: var(--gold-bg); color: var(--gold);
    font-size: 10px; font-weight: 700;
    border-radius: 99px; min-width: 18px; height: 18px;
    display: flex; align-items: center; justify-content: center; padding: 0 5px;
  }

  /* Divider */
  .cl-div { height: 1px; background: var(--c3); margin: 8px 11px; }

  /* Icon button */
  .cl-ibtn {
    width: 34px; height: 34px; border-radius: 8px;
    border: 1px solid var(--c3); background: var(--white);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all .13s;
    color: var(--stone); flex-shrink: 0;
  }
  .cl-ibtn:hover { background: var(--c2); color: var(--forest); }

  /* Avatar company */
  .cl-av {
    width: 34px; height: 34px; border-radius: 9px;
    background: var(--gold-bg); color: var(--gold);
    font-weight: 650; font-size: 12px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; border: 1.5px solid rgba(192,154,64,.3);
    cursor: pointer; transition: border-color .13s;
  }
  .cl-av:hover { border-color: var(--gold); }

  /* Breadcrumb btn */
  .cl-bc {
    background: none; border: none; padding: 0;
    font-size: 13px; font-family: 'DM Sans', sans-serif;
    color: var(--stone); cursor: pointer; transition: color .13s;
    text-decoration: none;
  }
  .cl-bc:hover { color: var(--forest); }

  /* User card bottom */
  .cl-ucard {
    display: flex; align-items: center; gap: 9px;
    padding: 8px 10px; border-radius: 9px;
    transition: background .13s; cursor: default;
  }
  .cl-ucard:hover { background: var(--c2); }

  /* Hamburger responsive */
  .cl-hamburger { display: flex; }
  @media (min-width: 768px) { .cl-hamburger { display: none !important; } }

  /* Dropdown */
  .cl-di {
    border-radius: 7px !important; font-size: 13.5px !important;
    color: var(--ink) !important; padding: 8px 10px !important;
    gap: 9px !important; cursor: pointer !important;
    font-family: 'DM Sans', sans-serif !important;
  }
  .cl-di:hover, .cl-di:focus { background: var(--c2) !important; color: var(--forest) !important; }
  .cl-di.red { color: #B83232 !important; }
  .cl-di.red:hover { background: #FEF0EE !important; }
`;

function CompanyStyles() {
  useEffect(() => {
    const id = 'cl-styles';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id; el.textContent = STYLES;
      document.head.appendChild(el);
    }
  }, []);
  return null;
}

// ─── Building mark (custom SVG, not AI icon) ──────────────────────────────────
function CompanyMark() {
  return (
    <div className="cl-mark">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <rect x="2"  y="6"  width="4" height="8" rx="0.5" fill="#C09A40" opacity="0.9"/>
        <rect x="6"  y="3"  width="4" height="11" rx="0.5" fill="#F5EECB"/>
        <rect x="10" y="5"  width="4" height="9"  rx="0.5" fill="#C09A40" opacity="0.7"/>
        <rect x="7"  y="10" width="2" height="4"  rx="0.3" fill="#C09A40"/>
      </svg>
    </div>
  );
}

// ─── Sidebar Nav ──────────────────────────────────────────────────────────────
function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const { user } = useCompanyAuth();

  const cls = (path: string) =>
    `cl-link${location.pathname === path ? ' on' : ''}`;

  return (
    <nav style={{ display:'flex', flexDirection:'column', gap:2, padding:'10px 9px' }}>
      <span className="cl-sec-label">Company Portal</span>

      <RouterNavLink to="/company/dashboard" className={() => cls('/company/dashboard')} onClick={onNavigate}>
        <LayoutDashboard size={15} strokeWidth={1.7} /> Dashboard
      </RouterNavLink>

      <RouterNavLink to="/company/submit" className={() => cls('/company/submit')} onClick={onNavigate}>
        <Plus size={15} strokeWidth={1.7} /> Submit Problem
      </RouterNavLink>

      <RouterNavLink to="/company/submissions" className={() => cls('/company/submissions')} onClick={onNavigate}>
        <FileText size={15} strokeWidth={1.7} /> My Submissions
      </RouterNavLink>

      <RouterNavLink to="/company/profile" className={() => cls('/company/profile')} onClick={onNavigate}>
        <Building2 size={15} strokeWidth={1.7} /> Company Profile
      </RouterNavLink>

      <RouterNavLink to="/company/settings" className={() => cls('/company/settings')} onClick={onNavigate}>
        <Settings size={15} strokeWidth={1.7} /> Settings
      </RouterNavLink>

      <div className="cl-div" />

      <RouterNavLink to="/company/support" className={() => cls('/company/support')} onClick={onNavigate}>
        <HelpCircle size={15} strokeWidth={1.7} /> Help &amp; Support
      </RouterNavLink>
    </nav>
  );
}

// ─── Breadcrumbs ──────────────────────────────────────────────────────────────
function Breadcrumbs() {
  const location = useLocation();
  const navigate  = useNavigate();
  const parts     = location.pathname.split('/').filter(Boolean);

  const crumbs: { label: string; path?: string }[] = [
    { label: 'Company', path: '/company/dashboard' },
  ];

  if      (parts[1] === 'dashboard')   crumbs.push({ label: 'Dashboard' });
  else if (parts[1] === 'submit')      crumbs.push({ label: 'Submit Problem' });
  else if (parts[1] === 'submissions') {
    crumbs.push({ label: 'My Submissions', path: '/company/submissions' });
    if (parts[2]) crumbs.push({ label: 'Submission Details' });
  }
  else if (parts[1] === 'profile')  crumbs.push({ label: 'Company Profile' });
  else if (parts[1] === 'settings') crumbs.push({ label: 'Settings' });
  else if (parts[1] === 'support')  crumbs.push({ label: 'Help & Support' });

  return (
    <div style={{ display:'flex', alignItems:'center', gap:4 }}>
      <Home size={13} style={{ color:'var(--stone)', opacity:.45, marginRight:2, flexShrink:0 }} />
      {crumbs.map((crumb, i) => (
        <span key={i} style={{ display:'flex', alignItems:'center', gap:4 }}>
          {i > 0 && <ChevronRight size={11} style={{ color:'var(--stone)', opacity:.35, flexShrink:0 }} />}
          {crumb.path && i < crumbs.length - 1
            ? <button className="cl-bc" onClick={() => navigate(crumb.path!)}>{crumb.label}</button>
            : <span style={{ fontSize:13, fontWeight:560, color:'var(--forest)' }}>{crumb.label}</span>
          }
        </span>
      ))}
    </div>
  );
}

// ─── Layout ───────────────────────────────────────────────────────────────────
export default function CompanyLayout() {
  const navigate   = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout, isLoading } = useCompanyAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/company/login');
  };

  const initials = (name: string) =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF8F4]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1C3830] mx-auto mb-4"></div>
          <p className="text-[#796F61]">Loading...</p>
        </div>
      </div>
    );
  }

  // If no user, redirect to login (though protected route should handle this)
  if (!user) {
    navigate('/company/login');
    return null;
  }

  const displayName = user.name || user.companyName || 'Company';
  const displayEmail = user.email || '';

  return (
    <>
      <CompanyStyles />
      <div className="cl" style={{ display:'flex', height:'100vh', width:'100%', overflow:'hidden' }}>

        {/* ── Desktop Sidebar ─────────────────────────────── */}
        <aside className="cl-aside">
          {/* Brand */}
          <div className="cl-brand">
            <CompanyMark />
            <span className="cl-brandname">Company Portal</span>
          </div>

          {/* Nav */}
          <div className="cl-scroll" style={{ flex:1, overflowY:'auto' }}>
            <SidebarNav />
          </div>

          {/* Company card */}
          <div style={{ borderTop:'1px solid var(--c3)', padding:'11px 9px', flexShrink:0 }}>
            <div className="cl-ucard">
              <div style={{ width:34, height:34, borderRadius:9, background:'var(--gold-bg)', border:'1.5px solid rgba(192,154,64,.3)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <Building2 size={16} style={{ color:'var(--gold)', strokeWidth:1.6 }} />
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:560, color:'var(--ink)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {user.companyName}
                </div>
                <div style={{ fontSize:11.5, color:'var(--stone)', marginTop:1 }}>
                  {user.name}
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* ── Main Column ──────────────────────────────────── */}
        <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>

          {/* Header */}
          <header className="cl-header">
            {/* Left */}
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              {/* Mobile hamburger */}
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <button className="cl-ibtn cl-hamburger">
                    <Menu size={16} />
                  </button>
                </SheetTrigger>
                <SheetContent side="left" style={{ width:252, padding:0, background:'var(--c)', border:'none', borderRight:'1px solid var(--c3)' }}>
                  <div style={{ height:60, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 18px', borderBottom:'1px solid var(--c3)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <CompanyMark />
                      <span style={{ fontFamily:"'Lora',serif", fontWeight:600, fontSize:14, color:'var(--forest)' }}>
                        Company Portal
                      </span>
                    </div>
                    <SheetClose asChild>
                      <button className="cl-ibtn"><X size={14} /></button>
                    </SheetClose>
                  </div>
                  <div className="cl-scroll" style={{ overflowY:'auto', height:'calc(100% - 60px)' }}>
                    <SidebarNav onNavigate={() => setMobileOpen(false)} />
                  </div>
                </SheetContent>
              </Sheet>

              <Breadcrumbs />
            </div>

            {/* Right — user menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="cl-av">
                  {initials(displayName)}
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" style={{ width:228, padding:6, background:'var(--white)', border:'1px solid var(--c3)', borderRadius:13, boxShadow:'0 10px 40px rgba(28,56,48,.10)' }}>
                {/* Company info */}
                <div style={{ padding:'10px 12px 13px', borderBottom:'1px solid var(--c3)', marginBottom:6 }}>
                  <div style={{ fontFamily:"'Lora',serif", fontWeight:600, fontSize:14, color:'var(--forest)', lineHeight:1.3 }}>
                    {user.name}
                  </div>
                  <div style={{ fontSize:11.5, color:'var(--stone)', marginTop:2 }}>{displayEmail}</div>
                  <span style={{ display:'inline-block', marginTop:7, fontSize:10.5, fontWeight:650, color:'var(--gold)', background:'var(--gold-bg)', borderRadius:5, padding:'2px 7px' }}>
                    {user.companyName}
                  </span>
                </div>

                <DropdownMenuItem className="cl-di" onClick={() => navigate('/company/profile')}>
                  <Building2 size={14} strokeWidth={1.7} style={{ color:'var(--stone)' }} /> Company Profile
                </DropdownMenuItem>
                <DropdownMenuItem className="cl-di" onClick={() => navigate('/company/settings')}>
                  <Settings size={14} strokeWidth={1.7} style={{ color:'var(--stone)' }} /> Settings
                </DropdownMenuItem>

                <div style={{ height:1, background:'var(--c3)', margin:'6px 4px' }} />

                <DropdownMenuItem className="cl-di red" onClick={handleLogout}>
                  <LogOut size={14} strokeWidth={1.7} /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>

          {/* Content */}
          <main style={{ flex:1, overflowY:'auto', background:'var(--c)', padding:'clamp(16px, 3vw, 40px)' }}>
            <div style={{ width:'100%' }}>
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </>
  );
}